import ArrowBack from '@mui/icons-material/ArrowBack';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Button, FormControl, FormHelperText, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { pwdRegexp, pwdRule } from '../../../shared/utilities';
import { invalidateUser } from '../../hooks/useUser';
import { postUserResetPassword } from '../../utilities/api';

type Props = {
    onFormChange: (nextForm: 'sent' | 'login') => void;
};

export const ResetForm = ({ onFormChange }: Props) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const initialValues = { password: '' };
    const validationSchema = yup.object().shape({
        password: yup.string().required('Required').matches(pwdRegexp, pwdRule),
    });

    const handleFormSubmit = async (values: typeof initialValues, { setErrors }: FormikHelpers<typeof initialValues>) => {
        try {
            const { token } = await postUserResetPassword(values);

            localStorage.setItem('auwx', token);
            invalidateUser(queryClient);
            navigate('/');
        } catch (_) {
            setErrors({ password: 'Unable to reset password.' });
        }
    };

    const handleFormChange = () => {
        onFormChange('login');
    };

    const handleShowPassword = () => setShowPassword((shown) => !shown);

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
            {({ values, errors, handleChange, handleBlur, touched, isSubmitting }) => {
                const isValid = pwdRegexp.test(values.password);

                return (
                    <Form noValidate autoComplete="off">
                        <Button variant="text" startIcon={<ArrowBack />} onClick={handleFormChange}>
                            Login
                        </Button>
                        <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(errors.password && touched.password)}>
                            <OutlinedInput
                                aria-describedby="password-text"
                                placeholder="Password"
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.password}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {values.password &&
                                            (isValid ? (
                                                <CheckCircle color="success" titleAccess="Password meets the requirements" />
                                            ) : (
                                                <Cancel color="error" titleAccess="Password does not meet the requirements" />
                                            ))}
                                        <IconButton aria-label={showPassword ? 'hide password' : 'show password'} edge="end" onClick={handleShowPassword}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                            <FormHelperText id="password-text" sx={isValid && !errors.password ? { color: 'success.main' } : undefined}>
                                {errors.password && touched.password ? errors.password : pwdRule}
                            </FormHelperText>
                        </FormControl>
                        <Button disabled={isSubmitting} type="submit" size="large" sx={{ mt: 2, mb: 2 }} fullWidth variant="contained">
                            Reset Password
                        </Button>
                    </Form>
                );
            }}
        </Formik>
    );
};
